/**
 * Gateway satellite ↔ draft mapping + the tab's pure ops. Kept out of
 * container-draft/reducer so each file stays one logical unit. The pool is
 * read through the shared lane reader (Phase 40): a legacy pool becomes
 * lanes with deterministic ids, a lane pool keeps its ids — so the dirty
 * compare of apiToDraft(persisted) vs draft stays honest. The lane ops live
 * in satellites/gateway-lanes-ops.
 * For Media Devoted | Created By Ciro Autiero
 */
import { defaultGatewayRanking, type DropGenGatewayDetail, type DropGenGatewayRanking, parseGatewayRanking, parseLanes, parseSenderAiMode, senderGroupIds } from '@trafficpulse/shared';

import type { DraftGateway, DraftPoolRow } from '../../../drop-generator.types';
import { applyLaneOp, type LaneOp } from './satellites/gateway-lanes-ops';

export type { LaneOp } from './satellites/gateway-lanes-ops';

const DEFAULT_SENDERS: DraftGateway['senderDefaults'] = { senderGroupId: null, mode: 'SHARED', amount: 8, aiMode: 'FALLBACK' };

export const DEFAULT_GATEWAY: DraftGateway = {
  strategy: 'HOLD_UNTIL_FAIL',
  orderMode: 'MANUAL',
  weightsMode: 'OFF',
  pool: [],
  senderDefaults: { ...DEFAULT_SENDERS },
  ranking: defaultGatewayRanking(),
};

export function gatewayToDraft(g: DropGenGatewayDetail | null): DraftGateway {
  if (!g) return { ...DEFAULT_GATEWAY, pool: [], senderDefaults: { ...DEFAULT_SENDERS }, ranking: defaultGatewayRanking() };
  return {
    strategy: g.strategy,
    orderMode: g.orderMode,
    weightsMode: g.weightsMode,
    pool: parsePool(g.poolJson, g.senderDefaultsJson),
    senderDefaults: parseSenderDefaults(g.senderDefaultsJson),
    ranking: parseGatewayRanking(g.orderFormulaJson),
  };
}

/** The AI senders' defaults; a legacy `staticSenders` list is NOT kept here — parsePool moves it onto the lanes. */
export function parseSenderDefaults(raw: unknown): DraftGateway['senderDefaults'] {
  if (typeof raw !== 'object' || raw === null) return { ...DEFAULT_SENDERS };
  const r = raw as Record<string, unknown>;
  return {
    senderGroupId: typeof r.senderGroupId === 'string' ? r.senderGroupId : null,
    ...(r.senderGroupIds !== undefined ? { senderGroupIds: senderGroupIds(r.senderGroupIds) } : {}),
    mode: r.mode === 'UNIQUE' ? 'UNIQUE' : 'SHARED',
    amount: typeof r.amount === 'number' && r.amount >= 1 && r.amount <= 15 ? Math.floor(r.amount) : DEFAULT_SENDERS.amount,
    aiMode: parseSenderAiMode(r.aiMode),
  };
}

/** The pool as lanes (the shared reader): malformed rows dropped, legacy static senders landed on their lanes. */
export const parsePool = (raw: unknown, senderDefaultsJson?: unknown): DraftPoolRow[] => parseLanes(raw, senderDefaultsJson);

export type GatewayOp =
  | {
      kind: 'patch';
      patch: Partial<Pick<DraftGateway, 'strategy' | 'orderMode' | 'weightsMode'>>;
    }
  | LaneOp
  | {
      kind: 'senderDefaults';
      patch: Partial<DraftGateway['senderDefaults']>;
    }
  | { kind: 'ranking'; ranking: DropGenGatewayRanking };

export function applyGatewayOp(g: DraftGateway, op: GatewayOp): DraftGateway {
  switch (op.kind) {
    case 'poolAdd':
    case 'poolRemove':
    case 'poolMove':
    case 'poolPatch':
    case 'laneSenders':
      return { ...g, pool: applyLaneOp(g.pool, op) };
    case 'patch':
      return { ...g, ...op.patch };
    case 'senderDefaults':
      return { ...g, senderDefaults: { ...g.senderDefaults, ...op.patch } };
    case 'ranking':
      return { ...g, ranking: op.ranking };
    default:
      return g;
  }
}
